import { gsap } from 'gsap';
import { BaseEffectModule } from '../BaseEffectModule.js';

/**
 * Basic Animations Module - CLEAN KEYFRAME VERSION
 * ✅ Pure keyframe support
 * ✅ GSAP timeline-based interpolation
 * ✅ Parallax-aware
 */
export class BasicAnimationsModule extends BaseEffectModule {
    constructor() {
        super('basicAnimations', {
            version: '2.0.0',
            description: 'Keyframe-based animation system'
        });
        
        this.propertyDefaults = {
            opacity: 1,
            x: 0,
            y: 0,
            rotation: 0,
            scale: 1,
            blur: 0,
            skewX: 0,
            skewY: 0,
            borderRadius: 0,
            clipProgress: 0,
            backgroundColor: 'transparent',
            color: 'inherit'
        };
    }
    
    /**
     * Convert x/y objects to CSS strings for GSAP
     */
    convertPropsForGSAP(props) {
        const converted = { ...props };
        
        if (converted.x && typeof converted.x === 'object') {
            converted.x = (converted.x.value || 0) + (converted.x.unit || 'px');
        }
        if (converted.y && typeof converted.y === 'object') {
            converted.y = (converted.y.value || 0) + (converted.y.unit || 'px');
        }
        
        return converted;
    }
    
    createState(element, config) {
        const basicConfig = config.basicAnimations;
        
        const hasParallax = element.classList.contains('unified-scroll-parallax') || 
                        element.classList.contains('unified-mouse-parallax');
        
        const keyframes = basicConfig.keyframes;
        const units = basicConfig.units || { x: 'px', y: 'px' }; // Fallback dla starych danych
        const timelineKeyframes = this.buildTimelineKeyframes(keyframes, hasParallax, units);
        
        const firstKeyframe = timelineKeyframes[0];
        if (firstKeyframe) {
            if (hasParallax) {
                const nonPositionProps = { ...firstKeyframe.props };
                delete nonPositionProps.x;
                delete nonPositionProps.y;
                
                // Convert x/y objects to CSS strings for GSAP
                Object.keys(nonPositionProps).forEach(prop => {
                    if (typeof nonPositionProps[prop] === 'object' && nonPositionProps[prop].value !== undefined) {
                        const propValue = nonPositionProps[prop];
                        nonPositionProps[prop] = (propValue.value || 0) + (propValue.unit || 'px');
                    }
                });
                
                gsap.set(element, nonPositionProps);
            } else {
                const props = this.convertPropsForGSAP(firstKeyframe.props);
                gsap.set(element, props);
            }
        }
        
        const state = {
            config: basicConfig,
            keyframes: timelineKeyframes,
            hasParallax: hasParallax,
            units: units,
            currentProgress: 0,
            targetProgress: 0,
            lastProgress: 0,
            smoothing: 0.1,
            isAnimating: false,
            needsUpdate: true,
            isScrubMode: false,
            scrubSmoothness: 0.15,
            timeline: null
        };
        
        return state;
    }
    
    /**
     * Build timeline keyframes with proper timing
     */
    buildTimelineKeyframes(rawKeyframes, hasParallax = false, units = {}) {
        if (!rawKeyframes || rawKeyframes.length === 0) {
            return [];
        }
        
        // Sort by position
        const sorted = [...rawKeyframes].sort((a, b) => a.position - b.position);
        
        return sorted.map(kf => {
            const props = this.prepareAnimationProps(kf.properties, hasParallax, units);
            
            return {
                position: kf.position,
                label: kf.label,
                props: props,
                normalizedPosition: kf.position / 100
            };
        });
    }
    
    update(element, state, sharedState, deltaTime, cache) {
        if (!this.isEnabled || !state.needsUpdate) return;
        
        const targetSmoothness = state.isScrubMode ? state.scrubSmoothness : state.smoothing;
        const hasProgressChange = this.isSignificantChange(state.currentProgress, state.targetProgress);
        
        if (hasProgressChange) {
            state.currentProgress += (state.targetProgress - state.currentProgress) * targetSmoothness;
            
            if (Math.abs(state.targetProgress - state.currentProgress) < 0.001) {
                state.currentProgress = state.targetProgress;
                state.needsUpdate = false;
            }
            
            this.applyKeyframeInterpolation(element, state);
            state.lastProgress = state.currentProgress;
        }
    }
    
    /**
     * Interpolate between keyframes based on progress
     */
    applyKeyframeInterpolation(element, state) {
        if (!state.keyframes || state.keyframes.length === 0) return;
        
        const progress = state.currentProgress;
        const keyframes = state.keyframes;
        
        // Find surrounding keyframes
        let beforeKf = keyframes[0];
        let afterKf = keyframes[keyframes.length - 1];
        
        for (let i = 0; i < keyframes.length - 1; i++) {
            const current = keyframes[i];
            const next = keyframes[i + 1];
            
            if (progress >= current.normalizedPosition && progress <= next.normalizedPosition) {
                beforeKf = current;
                afterKf = next;
                break;
            }
        }
        
        // Calculate local progress between the two keyframes
        let localProgress = 0;
        if (afterKf.normalizedPosition > beforeKf.normalizedPosition) {
            const range = afterKf.normalizedPosition - beforeKf.normalizedPosition;
            const offset = progress - beforeKf.normalizedPosition;
            localProgress = offset / range;
        }
        
        // Interpolate properties
        const interpolatedProps = this.interpolateKeyframeProps(
            beforeKf.props,
            afterKf.props,
            localProgress,
            state.units
        );
        
        // Never touch x,y if parallax is active
        if (state.hasParallax) {
            delete interpolatedProps.x;
            delete interpolatedProps.y;
        }
        
        if (Object.keys(interpolatedProps).length > 0) {
            const propsForGSAP = this.convertPropsForGSAP(interpolatedProps);
            gsap.set(element, propsForGSAP);
        }
    }
    
    /**
     * Interpolate between two keyframe property sets
     */
    interpolateKeyframeProps(beforeProps, afterProps, progress, units = {}) {
        const interpolated = {};
        
        const allProps = new Set([
            ...Object.keys(beforeProps),
            ...Object.keys(afterProps)
        ]);
        
        allProps.forEach(prop => {
            let beforeValue = beforeProps[prop];
            let afterValue = afterProps[prop];
            
            // Handle x and y with units - convert simple values to objects
            if (prop === 'x' || prop === 'y') {
                const unit = units[prop] || 'px';
                
                // Convert simple values to objects if needed
                if (beforeValue === undefined) {
                    beforeValue = { value: 0, unit: unit };
                } else if (typeof beforeValue === 'number') {
                    beforeValue = { value: beforeValue, unit: unit };
                } else if (typeof beforeValue === 'object' && beforeValue !== null) {
                    // Already an object, but ensure unit is from global units if not specified
                    beforeValue = { ...beforeValue, unit: beforeValue.unit || unit };
                }
                
                if (afterValue === undefined) {
                    afterValue = { value: 0, unit: unit };
                } else if (typeof afterValue === 'number') {
                    afterValue = { value: afterValue, unit: unit };
                } else if (typeof afterValue === 'object' && afterValue !== null) {
                    afterValue = { ...afterValue, unit: afterValue.unit || unit };
                }
            } else {
                // Handle other properties with defaults
                if (beforeValue === undefined) beforeValue = this.propertyDefaults[prop];
                if (afterValue === undefined) afterValue = this.propertyDefaults[prop];
            }
            
            interpolated[prop] = this.interpolateProperty(prop, beforeValue, afterValue, progress);
        });
        
        return interpolated;
    }
    
    /**
     * Prepare props with parallax awareness
     */
    prepareAnimationProps(rawProps, hasParallax = false, units = {}) {
        const props = {};
        
        // Skip position properties if parallax is active
        const allowedProps = hasParallax ? 
            ['opacity', 'rotation', 'scale', 'skewX', 'skewY'] : 
            ['opacity', 'x', 'y', 'rotation', 'scale', 'skewX', 'skewY'];
        
        allowedProps.forEach(prop => {
            if (rawProps[prop] !== undefined && rawProps[prop] !== null) {
                // Handle x and y - combine value with global units
                if (prop === 'x' || prop === 'y') {
                    const value = rawProps[prop];
                    const unit = units[prop] || 'px';
                    
                    // Handle migration - if value is object (old format), use its value
                    if (typeof value === 'object' && value !== null) {
                        props[prop] = { value: value.value, unit: value.unit || unit };
                    } else if (typeof value === 'number') {
                        props[prop] = { value: value, unit: unit };
                    }
                }
                // Handle other properties normally
                else {
                    props[prop] = rawProps[prop];
                }
            }
        });
        
        // Advanced properties
        this.handleAdvancedProperties(rawProps, props);
        
        return props;
    }
    
    /**
     * Handle advanced properties
     */
    handleAdvancedProperties(rawProps, props) {
        // Blur
        if (rawProps.blur !== undefined) {
            props.filter = `blur(${rawProps.blur}px)`;
        }
        
        // Border radius
        if (rawProps.borderRadius !== undefined) {
            props.borderRadius = `${rawProps.borderRadius}px`;
        }
        
        // Background color
        if (rawProps.backgroundColor && rawProps.backgroundColor !== 'transparent') {
            props.backgroundColor = rawProps.backgroundColor;
        }
        
        // Text color
        if (rawProps.color && rawProps.color !== 'inherit') {
            props.color = rawProps.color;
        }
        
        // Clip path
        if (rawProps.clipPath) {
            const progress = rawProps.clipProgress !== undefined ? rawProps.clipProgress : 100;
            const inverse = 100 - progress;
            
            let clipValue = rawProps.clipPath;
            clipValue = clipValue.replace(/{progress}/g, progress);
            clipValue = clipValue.replace(/{inverse}/g, inverse);
            
            props.clipPath = clipValue;
        }
    }
    
    /**
     * Set progress
     */
    setProgress(state, progress) {
        const clampedProgress = this.clamp(progress, 0, 1);
        if (this.isSignificantChange(state.targetProgress, clampedProgress)) {
            state.targetProgress = clampedProgress;
            state.needsUpdate = true;
            state.isScrubMode = true;
        }
    }
    
    /**
     * Build GSAP timeline animation from keyframes
     */
    buildKeyframeTimeline(element, state) {
        const timeline = gsap.timeline({
            paused: true,
            onComplete: () => {
                state.isAnimating = false;
                state.currentProgress = 1;
                state.targetProgress = 1;
                state.needsUpdate = false;
            }
        });
        
        const duration = state.config.timing.duration;
        const ease = state.config.timing.ease;
        
        // Add each keyframe to timeline
        state.keyframes.forEach((kf, index) => {
            if (index === 0) return;
            
            const previousKf = state.keyframes[index - 1];
            const segmentDuration = (kf.normalizedPosition - previousKf.normalizedPosition) * duration;
            
            const animProps = { ...kf.props };
            
            // Exclude position if parallax
            if (state.hasParallax) {
                delete animProps.x;
                delete animProps.y;
            }
            
            timeline.to(element, {
                ...this.convertPropsForGSAP(animProps),
                duration: segmentDuration,
                ease: ease
            });
        });
        
        // Handle looping
        if (state.config.timing.loop) {
            timeline.repeat(-1);
            timeline.repeatDelay(state.config.timing.loopDelay);
            timeline.yoyo(state.config.timing.yoyo);
        }
        
        return timeline;
    }
    
    /**
     * Animate to final using keyframe timeline
     */
    animateToFinal(element, state) {
        state.isAnimating = true;
        state.isScrubMode = false;
        
        // Build timeline if not exists
        if (!state.timeline) {
            state.timeline = this.buildKeyframeTimeline(element, state);
        }
        
        // Add delay
        const timeline = gsap.timeline();
        if (state.config.timing.delay > 0) {
            timeline.add(() => {}, state.config.timing.delay);
        }
        
        timeline.add(state.timeline.play());
        
        return timeline;
    }
    
    /**
     * Animate to initial - reverse to first keyframe
     */
    animateToInitial(element, state) {
        state.isAnimating = true;
        state.isScrubMode = false;
        
        const firstKeyframe = state.keyframes[0];
        if (!firstKeyframe) return;
        
        const animationProps = { ...firstKeyframe.props };
        
        if (state.hasParallax) {
            delete animationProps.x;
            delete animationProps.y;
        }
        
        const timeline = gsap.timeline({
            onComplete: () => {
                state.isAnimating = false;
                state.currentProgress = 0;
                state.targetProgress = 0;
                state.needsUpdate = false;
            }
        });
        
        timeline.to(element, {
            ...this.convertPropsForGSAP(animationProps),
            duration: state.config.timing.duration * 0.5,
            ease: state.config.timing.ease
        });
        
        return timeline;
    }
    
    interpolateProperty(prop, initialValue, finalValue, progress) {
        // if ( prop === 'clipPath' ) console.log( prop, initialValue, finalValue, progress )

        switch (prop) {
            case 'x':
            case 'y':
                return this.interpolateTransformUnit(initialValue, finalValue, progress);
                
            case 'backgroundColor':
            case 'color':
                return this.interpolateColor(initialValue, finalValue, progress);
                
            case 'filter':
                return this.interpolateFilter(initialValue, finalValue, progress);
                
            case 'clipPath':
                return this.interpolateClipPath(initialValue, finalValue, progress);
                
            default:
                if (typeof initialValue === 'number' && typeof finalValue === 'number') {
                    return this.lerp(initialValue, finalValue, progress);
                }
                return progress > 0.5 ? finalValue : initialValue;
        }
    }
    
    interpolateTransformUnit(initialValue, finalValue, progress) {
        // Handle null values
        if (!initialValue && !finalValue) return null;
        if (!initialValue) initialValue = { value: 0, unit: 'px' };
        if (!finalValue) finalValue = { value: 0, unit: 'px' };
        
        // Handle backward compatibility (numbers) - already converted in prepareAnimationProps
        if (typeof initialValue === 'number') {
            initialValue = { value: initialValue, unit: 'px' };
        }
        if (typeof finalValue === 'number') {
            finalValue = { value: finalValue, unit: 'px' };
        }
        
        // If units are the same, interpolate values
        if (initialValue.unit === finalValue.unit) {
            const interpolatedValue = this.lerp(initialValue.value || 0, finalValue.value || 0, progress);
            return { value: interpolatedValue, unit: initialValue.unit || 'px' };
        }
        
        // If units differ, snap to final at 50%
        return progress > 0.5 ? finalValue : initialValue;
    }
    
    interpolateColor(initial, final, progress) {
        if (!initial && !final) return 'transparent';
        if (!initial) initial = 'transparent';
        if (!final) final = 'transparent';
        
        if (initial === final) return initial;
        
        try {
            return gsap.utils.interpolate(initial, final, progress);
        } catch (error) {
            return progress > 0.5 ? final : initial;
        }
    }
    
    interpolateFilter(initial, final, progress) {
        const initialBlur = this.extractBlurValue(initial);
        const finalBlur = this.extractBlurValue(final);
        
        if (initialBlur !== null && finalBlur !== null) {
            const interpolatedBlur = this.lerp(initialBlur, finalBlur, progress);
            return `blur(${interpolatedBlur}px)`;
        }
        
        return progress > 0.5 ? final : initial;
    }
    
    extractBlurValue(filterString) {
        if (!filterString) return 0;
        if (filterString === 'none') return 0;
        
        const match = filterString.match(/blur\((\d+(?:\.\d+)?)px\)/);
        return match ? parseFloat(match[1]) : 0;
    }
    
    interpolateClipPath(initial, final, progress) {
        const parseInset = (str) => {
            const match = str.match(/inset\(([^)]+)\)/);
            return match ? match[1].split(/\s+/).map(v => parseFloat(v)) : null;
        };
        
        const initialVals = parseInset(initial); // [0, 100, 0, 0]
        const finalVals = parseInset(final);     // [0, 0, 0, 0]
        
        if (initialVals && finalVals) {
            // Find which position changed and interpolate only that
            const result = initialVals.map((start, i) => {
                const end = finalVals[i];
                return start !== end ? this.lerp(start, end, progress) : start;
            });
            
            return `inset(${result.map(v => v + '%').join(' ')})`;
        }
    }
    
    reset(element, state) {
        state.currentProgress = 0;
        state.targetProgress = 0;
        state.needsUpdate = true;
        state.isAnimating = false;
        state.isScrubMode = false;
        
        // Reset to first keyframe
        const firstKeyframe = state.keyframes[0];
        if (firstKeyframe) {
            const resetProps = { ...firstKeyframe.props };
            if (state.hasParallax) {
                delete resetProps.x;
                delete resetProps.y;
            }
            
            if (Object.keys(resetProps).length > 0) {
                const propsForGSAP = this.convertPropsForGSAP(resetProps);
                gsap.set(element, propsForGSAP);
            }
        }
        
        // Kill timeline if exists
        if (state.timeline) {
            state.timeline.kill();
            state.timeline = null;
        }
    }
    
    cleanup(state) {
        if (state.isAnimating) {
            gsap.killTweensOf(state.element);
        }
        
        if (state.timeline) {
            state.timeline.kill();
            state.timeline = null;
        }
        
        state.keyframes = null;
        state.config = null;
    }
}